'use client';

import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubscriptionTier } from '@/contexts/MondayAuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';

export interface UpgradeCTAProps {
  collapsed?: boolean;
}

export function UpgradeCTA({ collapsed = false }: UpgradeCTAProps) {
  const subscriptionTier = useSubscriptionTier();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Don't show for paid users
  if (subscriptionTier !== 'free') {
    return null;
  }

  const content = (
    <button
      onClick={() => setShowUpgradeModal(true)}
      className={cn(
        'block w-full text-left rounded-lg bg-[var(--highlight-primary)] border border-[var(--border-highlight-primary)]',
        'hover:bg-[var(--highlight-primary-strong)] transition-colors',
        collapsed ? 'p-2' : 'p-3'
      )}
    >
      {collapsed ? (
        <div className="flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary-600" />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-semibold text-[var(--text-heading)]">Upgrade to Pro</span>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-2">
            Unlimited operations, items, and recurring tasks.
          </p>
          <div className="text-sm font-bold text-primary-600">$29/month</div>
        </>
      )}
    </button>
  );

  // Wrap in tooltip when collapsed
  if (collapsed) {
    return (
      <>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent side="right">
              <div className="text-sm">
                <p className="font-semibold">Upgrade to Pro</p>
                <p className="text-[var(--text-muted)]">$29/month</p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} featureType="bulk_operation" />
      </>
    );
  }

  return (
    <>
      {content}
      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} featureType="bulk_operation" />
    </>
  );
}
